import { FC, useEffect, useMemo } from "react";
import { Box, Stack, TextField, useTheme } from "@mui/material";
import {
  DateRangePicker,
  FormikSelectField,
  FormikToggleField,
  Modal,
  ModalProps
} from "@vilocnv/allsetra-core";
import { Formik, FormikHelpers, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import {
  IObjectsOverviewFilters,
  objectsOverviewFiltersInitialValues,
  toFilterRangeEndIso,
  toFilterRangeStartIso,
  UNASSIGNED_GROUP_SENTINEL
} from "app/data/helpers/objectsOverviewFilterHelpers";

export type ObjectsOverviewFilterFormValues = IObjectsOverviewFilters & {
  showInactiveObjects: boolean;
};

export type ObjectsOverviewFilterFormProps = Pick<
  ModalProps,
  "open" | "onClose"
> & {
  initialValues?: ObjectsOverviewFilterFormValues;
  onSubmit: (
    values: ObjectsOverviewFilterFormValues,
    formikHelpers: FormikHelpers<ObjectsOverviewFilterFormValues>
  ) => void | Promise<void>;
  objectTypes: Array<{ uniqueId: string; name: string; fields?: any[] }>;
  groups: Array<{ uniqueId: string; name: string }>;
  groupsLoading?: boolean;
  objectTypesLoading?: boolean;
};

interface InnerFormProps {
  objectTypes: ObjectsOverviewFilterFormProps["objectTypes"];
  groups: ObjectsOverviewFilterFormProps["groups"];
  groupsLoading: boolean;
  objectTypesLoading: boolean;
}

const InnerForm: FC<InnerFormProps> = ({
  objectTypes,
  groups,
  groupsLoading,
  objectTypesLoading
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { values, setFieldValue } =
    useFormikContext<ObjectsOverviewFilterFormValues>();

  const selectedType = objectTypes.find(
    (type) => type.uniqueId === values.objectTypeId
  );
  const dynamicFields = (selectedType?.fields || []).filter(
    (field: any) => field?.uniqueId && field?.label
  );

  const groupOptions = useMemo(
    () => [
      { uniqueId: "", name: "Any group" },
      {
        uniqueId: UNASSIGNED_GROUP_SENTINEL,
        name: "No group (unassigned)"
      },
      ...groups
    ],
    [groups]
  );

  useEffect(() => {
    if (!values.objectTypeId) {
      setFieldValue("dynamicFieldId", "");
      setFieldValue("dynamicFieldValue", "");
    }
  }, [values.objectTypeId, setFieldValue]);

  const dateValue = {
    startDate: values.lastCommunicationStart
      ? new Date(values.lastCommunicationStart)
      : undefined,
    endDate: values.lastCommunicationEnd
      ? new Date(values.lastCommunicationEnd)
      : undefined
  };

  return (
    <Stack spacing={2}>
      <FormikSelectField
        theme={theme}
        label="Object type"
        name="objectTypeId"
        options={[{ uniqueId: "", name: "Any type" }, ...objectTypes]}
        optionLabelKey="name"
        optionValueKey="uniqueId"
        loading={objectTypesLoading}
        searchable
        fullWidth
      />

      <FormikSelectField
        theme={theme}
        label="Group"
        name="groupId"
        options={groupOptions}
        optionLabelKey="name"
        optionValueKey="uniqueId"
        loading={groupsLoading}
        searchable
        fullWidth
      />

      <Box>
        <DateRangePicker
          name="lastCommunication"
          theme={theme}
          placeholder="Last communication"
          value={dateValue}
          onChange={(range: { startDate?: Date; endDate?: Date }) => {
            setFieldValue(
              "lastCommunicationStart",
              range?.startDate ? toFilterRangeStartIso(range.startDate) : null
            );
            setFieldValue(
              "lastCommunicationEnd",
              range?.endDate ? toFilterRangeEndIso(range.endDate) : null
            );
          }}
          fullWidth
        />
      </Box>

      {dynamicFields.length > 0 && (
        <>
          <FormikSelectField
            theme={theme}
            label="Dynamic field"
            name="dynamicFieldId"
            options={[
              { uniqueId: "", label: "Any field" },
              ...dynamicFields.map((field: any) => ({
                uniqueId: field.uniqueId,
                label: field.label
              }))
            ]}
            optionLabelKey="label"
            optionValueKey="uniqueId"
            fullWidth
          />
          {values.dynamicFieldId ? (
            <TextField
              label="Dynamic field value"
              value={values.dynamicFieldValue}
              onChange={(event) =>
                setFieldValue("dynamicFieldValue", event.target.value)
              }
              fullWidth
              size="small"
            />
          ) : null}
        </>
      )}

      <FormikToggleField
        name="showInactiveObjects"
        label={t("common.showInactiveObjects") || "Show inactive objects"}
      />
    </Stack>
  );
};

const ObjectsOverviewFilterForm: FC<ObjectsOverviewFilterFormProps> = ({
  open,
  onClose,
  initialValues,
  onSubmit,
  objectTypes,
  groups,
  groupsLoading = false,
  objectTypesLoading = false
}) => {
  const theme = useTheme();

  return (
    <Formik
      initialValues={
        initialValues || {
          ...objectsOverviewFiltersInitialValues,
          showInactiveObjects: false
        }
      }
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ handleSubmit, isSubmitting, resetForm }) => (
        <Modal
          open={open}
          onClose={() => {
            resetForm();
            onClose();
          }}
          title="Filters"
          primaryBtnProps={{
            type: "submit",
            text: "Apply",
            loading: isSubmitting,
            // @ts-ignore
            onClick: handleSubmit
          }}
          secondaryBtnProps={{
            text: "Cancel",
            onClick: () => {
              resetForm();
              onClose();
            }
          }}
          theme={theme}
        >
          <InnerForm
            objectTypes={objectTypes}
            groups={groups}
            groupsLoading={groupsLoading}
            objectTypesLoading={objectTypesLoading}
          />
        </Modal>
      )}
    </Formik>
  );
};

export default ObjectsOverviewFilterForm;
